import { cn } from "@/lib/utils";

const ACCENTS = {
  cyan: { c: "var(--yb-cyan)", g: "var(--yb-cyan-glow)" },
  magenta: { c: "var(--yb-magenta)", g: "var(--yb-magenta-glow)" },
  amber: { c: "var(--yb-amber)", g: "rgba(255,177,61,0.16)" },
  mint: { c: "var(--yb-mint)", g: "var(--yb-mint-glow)" },
  violet: { c: "var(--yb-violet)", g: "rgba(155,140,255,0.16)" },
};
export function accent(name) {
  return ACCENTS[name] || ACCENTS.cyan;
}

/* ── Panel: the base HUD surface ── */
export function Panel({ children, className, glow = false, scan = false, as: Tag = "div", ...rest }) {
  return (
    <Tag
      className={cn("hud-panel p-4 sm:p-5", glow && "glow-cyan", scan && "scanline", className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/* ── Section label: monospace eyebrow with tick marks ── */
export function Label({ children, accent: a = "cyan", className }) {
  const { c } = accent(a);
  return (
    <div
      className={cn("font-mono text-[10px] uppercase tracking-[0.22em] flex items-center gap-2", className)}
      style={{ color: "var(--yb-text-muted)" }}
    >
      <span style={{ width: 14, height: 1, background: c, display: "inline-block" }} />
      {children}
    </div>
  );
}

/* ── Status pill ── */
export function Pill({ children, tone = "cyan", className }) {
  const { c } = accent(tone);
  return (
    <span className={cn("pill", className)} style={{ color: c }}>
      <span className="dot" style={{ width: 6, height: 6 }} />
      {children}
    </span>
  );
}

/* ── Stat readout ── */
export function Stat({ label, value, sub, accent: a = "cyan", mono = true }) {
  const { c } = accent(a);
  return (
    <div className="flex flex-col gap-1">
      <Label accent={a}>{label}</Label>
      <div
        className={cn("text-[22px] sm:text-[26px] leading-none", mono ? "font-mono" : "font-display")}
        style={{ color: c }}
      >
        {value}
      </div>
      {sub != null && (
        <div className="text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

/* ── Section heading ── */
export function Heading({ title, kicker, right, accent: a = "cyan" }) {
  return (
    <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
      <div>
        {kicker && <Label accent={a} className="mb-1.5">{kicker}</Label>}
        <h2 className="font-display text-[18px] sm:text-[22px]" style={{ color: "var(--yb-text)" }}>
          {title}
        </h2>
      </div>
      {right}
    </div>
  );
}

/* ── Empty state ── */
export function Empty({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-4 gap-3">
      {Icon && (
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ border: "1px solid var(--yb-hairline)", color: "var(--yb-text-muted)" }}
        >
          <Icon size={22} />
        </div>
      )}
      <div className="font-head text-[15px]" style={{ color: "var(--yb-text-dim)" }}>{title}</div>
      {hint && <div className="text-[12px] max-w-sm" style={{ color: "var(--yb-text-muted)" }}>{hint}</div>}
      {action}
    </div>
  );
}

/* ── Spinner ── */
export function Spinner({ size = 18 }) {
  return (
    <span
      className="yb-spin inline-block rounded-full"
      style={{
        width: size, height: size,
        border: "2px solid var(--yb-hairline)",
        borderTopColor: "var(--yb-cyan)",
      }}
      aria-label="loading"
    />
  );
}

export function Loading({ label = "Syncing telemetry…" }) {
  return (
    <div className="flex items-center gap-3 py-10 justify-center" style={{ color: "var(--yb-text-muted)" }}>
      <Spinner />
      <span className="font-mono text-[12px] tracking-widest uppercase">{label}</span>
    </div>
  );
}

/* ── Button ── */
export function HudButton({ children, tone = "cyan", variant = "solid", className, ...rest }) {
  const { c } = accent(tone);
  const base = "inline-flex items-center justify-center gap-2 font-head text-[13px] font-semibold px-4 h-9 rounded-[5px] transition-all disabled:opacity-40 disabled:cursor-not-allowed";
  if (variant === "solid") {
    return (
      <button
        className={cn(base, className)}
        style={{ background: c, color: "#04101b", boxShadow: `0 0 22px -6px ${c}` }}
        {...rest}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      className={cn(base, "hover:bg-white/5", className)}
      style={{ color: c, border: `1px solid ${c}`, background: "transparent" }}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ── Progress bar ── */
export function Bar({ value = 0, max = 100, tone = "cyan" }) {
  const { c } = accent(tone);
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--yb-panel-3)" }}>
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: c, boxShadow: `0 0 12px ${c}` }} />
    </div>
  );
}
